import React, { useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import { useData } from '../../context/DataContext';
import { combinedOdds, entryResult } from '../../lib/tips';
import TipsterLogo from '../TipsterLogo';
import EntryForm from './EntryForm';

const COLOR = { WIN: 'text-win', LOST: 'text-loss', PENDING: 'text-royal', NONE: 'text-slate-400' };

export default function ArchiveManager() {
  const { tipsters, entriesFor, addEntry, updateEntry, deleteEntry } = useData();
  const [tipsterId, setTipsterId] = useState(tipsters[0]?.id);
  const [editing, setEditing] = useState(null); // null | 'new' | entry
  const tipster = tipsters.find(t => t.id === tipsterId) || tipsters[0];
  if (!tipster) return null;
  const list = entriesFor(tipster.id);

  const save = async (payload) => {
    try {
      if (editing === 'new') await addEntry(payload);
      else await updateEntry(editing.id, payload);
      toast({ title: 'Saved', description: `${tipster.name} · ${payload.date} saved.` });
      setEditing(null);
    } catch (e) {
      toast({ title: 'Could not save', description: e?.response?.data?.detail || 'Please check the form.', variant: 'destructive' });
    }
  };

  const remove = async (entry) => {
    if (!window.confirm(`Delete ${tipster.name} entry for ${entry.date}?`)) return;
    await deleteEntry(entry.id);
    toast({ title: 'Deleted', description: `${entry.date} removed from the archive.` });
  };

  const tid = (e) => `${e.tipster_id}-${e.date.replace(/\./g, '-')}`;

  return (
    <div data-testid="admin-archive">
      <div className="flex flex-wrap gap-2 mb-5">
        {tipsters.map(t => (
          <button key={t.id} onClick={() => setTipsterId(t.id)} className={`flex items-center gap-2 pl-1.5 pr-4 py-1.5 rounded-full border text-sm font-semibold transition-colors ${tipster.id === t.id ? 'bg-navy text-white border-navy' : 'bg-white border-line text-slate-600 hover:border-royal'}`} data-testid={`admin-archive-tab-${t.id}`}>
            <TipsterLogo tipster={t} className="w-7 h-7" /> {t.name}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between gap-4 mb-5">
        <div>
          <h2 className="font-display text-xl font-bold text-navy">{tipster.name} archive</h2>
          <p className="text-sm text-slate-500">{list.length} days recorded · {tipster.format === 'double' ? '2 matches per day, combined odds auto-calculated' : '1 selection per day'}.</p>
        </div>
        <Button onClick={() => setEditing('new')} className="bg-royal hover:bg-royal-dark text-white rounded-full" data-testid="admin-add-entry"><Plus className="w-4 h-4 mr-1.5" /> Add day</Button>
      </div>

      <div className="rounded-2xl border border-line bg-white divide-y divide-line overflow-hidden">
        {list.map(e => {
          const r = entryResult(e);
          return (
            <div key={e.id} className="flex items-center gap-4 px-4 py-3 hover:bg-mist/60" data-testid={`admin-entry-${tid(e)}`}>
              <div className="font-mono text-sm font-bold text-navy w-24">{e.date}</div>
              <div className="flex-1 min-w-0 text-sm text-slate-700 truncate">
                {e.matches.length ? e.matches.map(m => `${m.home} vs ${m.away} (${m.pick} @ ${Number(m.odd).toFixed(2)})`).join('  ·  ') : <span className="italic text-slate-400">No pick</span>}
              </div>
              <div className="font-mono text-sm w-14 text-right text-navy">{combinedOdds(e.matches)?.toFixed(2) || '—'}</div>
              <div className={`font-mono text-xs font-bold w-16 text-right ${COLOR[r]}`}>{r === 'NONE' ? '—' : r}</div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => setEditing(e)} data-testid={`admin-edit-${tid(e)}`}><Pencil className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove(e)} className="text-loss hover:text-loss" data-testid={`admin-delete-${tid(e)}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          );
        })}
        {list.length === 0 && <div className="px-4 py-6 text-sm text-slate-500">No entries yet for {tipster.name}.</div>}
      </div>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing === 'new' ? `Add a day — ${tipster.name}` : `Edit ${editing?.date} — ${tipster.name}`}</DialogTitle></DialogHeader>
          {editing !== null && <EntryForm key={editing === 'new' ? 'new' : editing.id} tipster={tipster} initial={editing === 'new' ? null : editing} onSave={save} onCancel={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
